// Rules for the support composer that don't need the DOM, kept here so they can be tested.

export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
// Logistic and its nginx reject a request over 30 MB, and nginx's 413 reaches the browser as a network
// error. Attachments may add up to 24 MB, leaving room for a voice note and the form fields.
export const MAX_TOTAL_FILE_BYTES = 24 * 1024 * 1024;
export const MAX_REQUEST_BYTES = 28 * 1024 * 1024;
export const MAX_TEXT = 4000;
// Photos are re-encoded before upload (drops EXIF such as GPS) and scaled to fit this.
export const MAX_IMAGE_EDGE = 2560;
// The server's limits apply to the re-encoded copy, usually far smaller; this only guards decoding memory.
export const MAX_SOURCE_PHOTO_BYTES = 30 * 1024 * 1024;
// A press shorter than this on the mic is a tap (dictate); longer is a hold (send on release).
export const HOLD_THRESHOLD_MS = 350;
// Below this the recording is treated as silence and not sent (speech models invent text for silence).
export const SILENCE_LEVEL = 0.04;
export const MIN_SPEECH_MS = 700;
// Moving this far from the mic during a hold cancels it.
export const CANCEL_DISTANCE_PX = 60;
// Signed-out visitors (landing page) send text only, within the server's visitor limits.
export const VISITOR_MAX_TEXT = 2000;
export const VISITOR_MAX_EMAIL = 200;
export const VISITOR_MAX_NAME = 120;
export const VISITOR_MAX_COMPANY = 160;
export const VISITOR_PLACEHOLDER = 'Questions about Lunch.Team or signing in?';
// What a visitor is told, by the composer or from the server's answer (supportApi).
export const VISITOR_NAME_MISSING = 'Enter your name.';
export const VISITOR_EMAIL_INVALID =
  "That email address doesn't look right. Please check it and try again.";
export const VISITOR_CONVERSATION_FULL =
  'This chat is full. Start a new conversation, or sign in.';

// One plain address, as the server takes it (SupportVisitorService.PLAIN_EMAIL): a dot-atom local part,
// '@', and host-name labels joined by dots, with at least one dot. No quoted parts, comments or IP literals.
const EMAIL_ATOM = "[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+";
const EMAIL_LABEL = '[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?';
const VISITOR_EMAIL = new RegExp(
  `^${EMAIL_ATOM}(?:\\.${EMAIL_ATOM})*@${EMAIL_LABEL}(?:\\.${EMAIL_LABEL})+$`
);

export function isValidVisitorEmail(value) {
  const email = (value || '').trim();
  return (
    !!email && email.length <= VISITOR_MAX_EMAIL && VISITOR_EMAIL.test(email)
  );
}

const FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/gif',
  'application/pdf',
];
const TYPE_BY_EXTENSION = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heic',
  gif: 'image/gif',
  pdf: 'application/pdf',
};

export function fileType(file) {
  if (file.type && FILE_TYPES.indexOf(file.type) >= 0) {
    return file.type;
  }
  if (file.type === 'image/heif') {
    return 'image/heic';
  }
  const match = /\.([a-z0-9]+)$/i.exec(file.name || '');
  return (!file.type || file.type === 'application/octet-stream') && match
    ? TYPE_BY_EXTENSION[match[1].toLowerCase()] || null
    : null;
}

// Photos the composer re-encodes before upload; PDFs go as picked. GIF too (its first frame, as PNG):
// otherwise a JPEG renamed .gif would keep its EXIF and GPS.
const REENCODE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/gif',
];

export function isReencoded(type) {
  return REENCODE_TYPES.indexOf(type) >= 0;
}

/**
 * Splits picked files into ones we can send and messages for the rest.
 * Files with a missing browser type (e.g. HEIC on some browsers) are re-typed from their extension.
 * Files sent as picked are held to the server's limits here. Photos are re-encoded first, so their
 * limits are checked on the copy when sending (uploadSizeProblem).
 * existingBytes is the upload size already known for what is attached, for the total cap.
 */
export function acceptFiles(existingCount, picked, existingBytes = 0) {
  const accepted = [];
  const errors = [];
  let totalBytes = existingBytes;
  (picked || []).forEach(file => {
    const type = fileType(file);
    const photo = isReencoded(type);
    if (!type) {
      errors.push(`${file.name}: only photos and PDFs can be attached.`);
    } else if (photo && file.size > MAX_SOURCE_PHOTO_BYTES) {
      errors.push(`${file.name}: photos must be 30 MB or smaller.`);
    } else if (!photo && file.size > MAX_FILE_BYTES) {
      errors.push(`${file.name}: files must be 10 MB or smaller.`);
    } else if (existingCount + accepted.length >= MAX_FILES) {
      errors.push(`You can attach up to ${MAX_FILES} files.`);
    } else if (!photo && totalBytes + file.size > MAX_TOTAL_FILE_BYTES) {
      errors.push('Attachments can add up to 24 MB in total.');
    } else {
      if (!photo) totalBytes += file.size;
      accepted.push(type === file.type ? file : retype(file, type));
    }
  });
  return {
    accepted,
    errors: errors.filter(
      (message, index) => errors.indexOf(message) === index
    ),
  };
}

function retype(file, type) {
  try {
    return new File([file], file.name, {
      type,
      lastModified: file.lastModified,
    });
  } catch (e) {
    return file;
  }
}

export function classifyPress(durationMs) {
  return durationMs < HOLD_THRESHOLD_MS ? 'tap' : 'hold';
}

export function heardSpeech(recording) {
  return (
    !!recording &&
    recording.durationMs >= MIN_SPEECH_MS &&
    recording.peakLevel >= SILENCE_LEVEL
  );
}

export function movedToCancel(start, point) {
  if (!start || !point) return false;
  const dx = point.x - start.x;
  const dy = point.y - start.y;
  return Math.sqrt(dx * dx + dy * dy) > CANCEL_DISTANCE_PX;
}

// Short enough for one line at 320px wide with 44px touch buttons (about 175px of text).
export function placeholderFor(screenId) {
  switch (screenId) {
    case 'order.tracking':
    case 'order.detail':
    case 'orders.list':
      return 'Problem with an order?';
    case 'checkout.cart':
    case 'checkout.result':
      return 'Basket or payment?';
    case 'menu.restaurant':
      return 'Menu question?';
    case 'account.profile':
      return 'Account question?';
    default:
      return 'How can we help?';
  }
}

// Multipart encoding sends each line break as CR LF, and the server counts both characters.
export function encodedTextLength(text) {
  const value = text || '';
  return value.length + (value.match(/\n/g) || []).length;
}

/**
 * Checked on what is actually uploaded (re-encoded photos, the voice note) before sending:
 * a message for a file or a message over the server's limits, or null when it can go.
 */
export function uploadSizeProblem(files, voiceBytes = 0) {
  const uploads = files || [];
  const big = uploads.filter(file => file.size > MAX_FILE_BYTES);
  if (big.length) {
    return `${big
      .map(file => file.name)
      .join(', ')}: files must be 10 MB or smaller. Remove ${
      big.length > 1 ? 'them' : 'it'
    } and try again.`;
  }
  const total = uploads.reduce((sum, file) => sum + file.size, voiceBytes);
  if (total > MAX_REQUEST_BYTES) {
    return "That's too much for one message. Remove a photo or two and try again.";
  }
  return null;
}

/** Why a photo was taken off the message when it couldn't be re-encoded (see sanitizeImage). */
export function preparationProblem(fileName, error) {
  return error && error.code === 'heic'
    ? `${fileName}: this browser can't open HEIC photos. Attach a JPEG or a screenshot instead.`
    : `${fileName}: we couldn't prepare this photo, so it wasn't attached. Try again, or attach a screenshot.`;
}

export function formatDuration(ms) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${rest < 10 ? '0' : ''}${rest}`;
}

export function newClientReportId() {
  const random =
    typeof window !== 'undefined' &&
    window.crypto &&
    window.crypto.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint32Array(2)), n =>
          n.toString(36)
        ).join('')
      : Math.random()
          .toString(36)
          .slice(2) +
        Math.random()
          .toString(36)
          .slice(2);
  return `web-${Date.now().toString(36)}-${random}`;
}

/** Appends dictated words to what the customer has already typed. */
export function appendDictation(text, words) {
  const spoken = (words || '').trim();
  if (!spoken) return text;
  if (!text || !text.trim()) return spoken;
  return /\s$/.test(text) ? text + spoken : `${text} ${spoken}`;
}

// Transcripts still being made: keep checking the conversation until they finish.
export function hasPendingTranscripts(conversation) {
  return ((conversation && conversation.messages) || []).some(message =>
    (message.attachments || []).some(
      attachment =>
        attachment.kind === 'AUDIO' &&
        ['PENDING', 'PROCESSING', 'RETRY', 'CONFIGURATION_REQUIRED'].indexOf(
          attachment.transcriptStatus
        ) >= 0
    )
  );
}

// Frees the canvas's memory now: iOS Safari counts every canvas against one cap until garbage collection.
function releaseCanvas(canvas) {
  canvas.width = 0;
  canvas.height = 0;
}

// Small square preview as a data: URL. The site's Content-Security-Policy allows data: images but not blob: URLs.
// Resolves null when the browser can't decode the image; the chip then shows a file icon.
export function makeThumbnail(file, size = 56) {
  const draw = (source, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const scale = Math.max(size / width, size / height);
    const drawWidth = width * scale;
    const drawHeight = height * scale;
    const context = canvas.getContext && canvas.getContext('2d');
    if (!context) return null;
    // White behind transparent pixels, as in the uploaded JPEG.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size, size);
    context.drawImage(
      source,
      (size - drawWidth) / 2,
      (size - drawHeight) / 2,
      drawWidth,
      drawHeight
    );
    const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
    releaseCanvas(canvas);
    return thumbnail;
  };
  const viaElement = () =>
    new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => {
        const image = new Image();
        image.onload = () =>
          resolve(draw(image, image.naturalWidth, image.naturalHeight));
        image.onerror = () => resolve(null);
        image.src = reader.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  if (typeof window === 'undefined' || file.type.indexOf('image/') !== 0) {
    return Promise.resolve(null);
  }
  if (typeof window.createImageBitmap === 'function') {
    return window
      .createImageBitmap(file)
      .then(bitmap => {
        const thumbnail = draw(bitmap, bitmap.width, bitmap.height);
        if (typeof bitmap.close === 'function') bitmap.close();
        return thumbnail;
      })
      .catch(() => viaElement())
      .catch(() => null);
  }
  return viaElement().catch(() => null);
}

function preparationError(code) {
  const error = new Error(`The photo could not be re-encoded (${code}).`);
  error.code = code;
  return error;
}

// The photo decoded and upright (EXIF orientation applied), ready to draw.
function decodePhoto(file) {
  const viaElement = () =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const image = new Image();
        image.onload = () =>
          resolve({
            source: image,
            width: image.naturalWidth,
            height: image.naturalHeight,
            close() {},
          });
        image.onerror = reject;
        image.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  if (typeof window.createImageBitmap !== 'function') return viaElement();
  return new Promise(resolve =>
    resolve(window.createImageBitmap(file, { imageOrientation: 'from-image' }))
  ).then(
    bitmap => ({
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      close() {
        if (typeof bitmap.close === 'function') bitmap.close();
      },
    }),
    () => viaElement()
  );
}

// Draws the photo at most MAX_IMAGE_EDGE on its longer side and encodes it as type.
function encodePhoto(photo, type) {
  return new Promise((resolve, reject) => {
    if (!photo.width || !photo.height) {
      reject(new Error('Nothing to draw.'));
      return;
    }
    const scale = Math.min(
      1,
      MAX_IMAGE_EDGE / Math.max(photo.width, photo.height)
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(photo.width * scale));
    canvas.height = Math.max(1, Math.round(photo.height * scale));
    const context = canvas.getContext && canvas.getContext('2d');
    if (!context || typeof canvas.toBlob !== 'function') {
      releaseCanvas(canvas);
      reject(new Error('No canvas.'));
      return;
    }
    if (type === 'image/jpeg') {
      // JPEG has no transparency: without a fill, transparent pixels turn black.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(photo.source, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      blob => {
        releaseCanvas(canvas);
        if (blob && blob.size) resolve(blob);
        else reject(new Error('Nothing encoded.'));
      },
      type,
      0.85
    );
  });
}

function asUpload(blob, file) {
  const type = blob.type || 'image/jpeg';
  const name =
    (file.name || 'photo').replace(/\.[a-z0-9]+$/i, '') +
    (type === 'image/png' ? '.png' : '.jpg');
  try {
    return new File([blob], name, { type, lastModified: file.lastModified });
  } catch (e) {
    // No File constructor (old Edge): the blob goes under this name.
    blob.name = name;
    return blob;
  }
}

/**
 * A copy of a photo without its metadata (EXIF GPS location, device, time), scaled to MAX_IMAGE_EDGE.
 * PNG stays PNG (transparency) unless that copy is bigger than the original or over 10 MB; then it
 * becomes JPEG on white, like the other photos. GIF and PDF are returned unchanged.
 * Rejects when a photo can't be re-encoded, so it is never uploaded with its metadata: error.code is
 * 'heic' for a HEIC photo this browser can't open (Safari can), 'failed' for anything else.
 */
export function sanitizeImage(file) {
  // The type from the extension when the browser gave none: where there is no File constructor to re-type
  // the pick (old Edge), an untyped .jpg must still be re-encoded, not sent with its EXIF.
  const type = fileType(file) || file.type;
  if (!isReencoded(type)) return Promise.resolve(file);
  const decodeFailure = type === 'image/heic' ? 'heic' : 'failed';
  if (typeof window === 'undefined') {
    return Promise.reject(preparationError(decodeFailure));
  }
  return decodePhoto(file).then(
    photo => {
      const jpeg = () => encodePhoto(photo, 'image/jpeg');
      const encoded =
        type === 'image/png' || type === 'image/gif'
          ? encodePhoto(photo, 'image/png').then(png =>
              png.size > MAX_FILE_BYTES || png.size > file.size ? jpeg() : png
            )
          : jpeg();
      return encoded.then(
        blob => {
          photo.close();
          return asUpload(blob, file);
        },
        () => {
          photo.close();
          throw preparationError('failed');
        }
      );
    },
    () => {
      throw preparationError(decodeFailure);
    }
  );
}

/**
 * What changed between two reads of the conversation, for screen-reader announcements and the unread dot:
 * replies is the number of new messages from support (assistantReplies of them from the Lunch.Team
 * assistant); transcribed the number of voice notes that finished.
 */
export function conversationChanges(previous, next) {
  const before = (previous && previous.messages) || [];
  const after = (next && next.messages) || [];
  const known = {};
  const finishedBefore = {};
  before.forEach(message => {
    known[message.messageId] = true;
    (message.attachments || []).forEach(attachment => {
      if (
        attachment.transcriptStatus === 'COMPLETED' ||
        attachment.transcriptStatus === 'FAILED'
      ) {
        finishedBefore[attachment.attachmentId] = true;
      }
    });
  });
  let replies = 0;
  let assistantReplies = 0;
  let transcribed = 0;
  after.forEach(message => {
    if (!known[message.messageId] && message.authorType !== 'REQUESTER') {
      replies++;
      if (message.authorType === 'ASSISTANT') assistantReplies++;
    }
    (message.attachments || []).forEach(attachment => {
      if (
        attachment.kind === 'AUDIO' &&
        attachment.transcriptStatus === 'COMPLETED' &&
        !finishedBefore[attachment.attachmentId] &&
        known[message.messageId]
      ) {
        transcribed++;
      }
    });
  });
  return { replies, assistantReplies, transcribed };
}



// WEBPACK FOOTER //
// ./src/support/composerModel.js